import { useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { getSuggestions } from '@gonfalon/openapi/operations/getSuggestions';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Code,
  ComboBox,
  FieldGroup,
  Group,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import { RequiredLabel } from '../components/RequiredLabel';
import type { MetricForm } from '../formUtils';

import styles from './NumericSpanAttributeField.module.css';

interface NumericSpanAttributeFieldProps {
  control: Control<MetricForm>;
  /** Id of a label the caller renders instead, such as a row's label in the sectioned layout. */
  'aria-labelledby'?: string;
}

const FIELD_NAME = 'traceValueLocation';

export const NumericSpanAttributeField: React.FC<NumericSpanAttributeFieldProps> = ({
  control,
  'aria-labelledby': labelledBy,
}) => {
  const projectKey = useProjectKey();
  // Same getSuggestions request shape (and queryKey) as MetricSourceQueryBuilder
  // so the two share react-query cache. No filters — every suggested span
  // attribute is surfaced as an option, with item.field used as label/value.
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  const { data } = useQuery({
    queryFn: async () =>
      getSuggestions({
        body: {
          projectKey,
          dataSource: {
            domain: 'observability',
            kind: 'traces',
            timeRange: {
              from: sevenDaysAgo,
              to: now,
              timezone: 'America/New_York',
            },
          },
          suggestFields: {
            limit: 100,
            withValues: {
              limit: 100,
            },
          },
        },
      }),
    queryKey: ['suggestions', projectKey],
  });
  const options = useMemo(
    () =>
      (data?.data?.items ?? [])
        .map((item) => item.field)
        .filter((f): f is string => Boolean(f))
        .map((field) => ({ id: field, key: field, textValue: field })),
    [data],
  );

  return (
    <Controller
      control={control}
      name={FIELD_NAME}
      rules={{ required: msg('metrics.metricDefinition.spanAttributeRequired') }}
      shouldUnregister
      render={({ field, fieldState: { error } }) => (
        <FieldGroup errorMessage={error?.message}>
          {labelledBy === undefined && (
            <RequiredLabel label={msg('metrics.metricDefinition.spanAttributeLabel')} htmlFor={FIELD_NAME} />
          )}
          <div className={styles.fieldDescription}>
            <Message
              id="metrics.metricDefinition.spanAttributeHelp"
              values={{ code: (chunks) => <Code>{chunks}</Code> }}
            />
          </div>
          {/* `allowsCustomValue` accepts any attribute path, not just the suggested ones, so mirror
           * the typed `inputValue` to the form on every keystroke. */}
          <ComboBox
            aria-label={labelledBy === undefined ? msg('metrics.metricDefinition.spanAttributeRowLabel') : undefined}
            aria-labelledby={labelledBy}
            allowsCustomValue
            isInvalid={!!error}
            selectedKey={options.some((o) => o.id === field.value) ? (field.value ?? null) : null}
            inputValue={field.value ?? ''}
            onInputChange={(value) => field.onChange(value)}
            onSelectionChange={(key) => {
              if (typeof key === 'string') {
                field.onChange(key);
              }
            }}
          >
            <Group>
              <Input id={FIELD_NAME} placeholder={msg('metrics.metricDefinition.spanAttributePlaceholder')} />
              <IconButton
                aria-label={msg('metrics.metricDefinition.showSpanAttributesLabel')}
                icon="chevron-down"
                size="small"
                variant="minimal"
              />
            </Group>
            <Popover>
              <ListBox
                items={options}
                selectionMode="single"
                aria-label={msg('metrics.metricDefinition.spanAttributesListLabel')}
              >
                {(item) => (
                  <ListBoxItem key={item.key} id={item.id} textValue={item.textValue}>
                    {item.textValue}
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </ComboBox>
        </FieldGroup>
      )}
    />
  );
};
